import React, {useContext, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import {Grid} from "@mui/material";

import CustomButton from "components/CustomButton";
import WmsItemInformation from "components/WMS/Mobile/WmsItemInformation/WmsItemInformation";

import {packingStepNames} from "../../packingSteps";

import {PackingContext} from "../../PackingContext";

WmsItemInformationPacking.propTypes = {
    lineItem: PropTypes.object.isRequired,
    packingStep: PropTypes.object.isRequired,
    beginPacking: PropTypes.func.isRequired,
    beginShipmentWeigh: PropTypes.func.isRequired,
    handleClose: PropTypes.func,
    handleProductCancelled: PropTypes.func,
};

function WmsItemInformationPacking({lineItem, packingStep, beginPacking, beginShipmentWeigh, handleProductCancelled, handleClose}) {
    const {
        setOpenProductProblemDrawer,
        handleAllItemAreCancelled,
        handlePackItem,
        packingLoading
    } = useContext(PackingContext);


    const areAllItemsPackedOrCancelled = packingStep.selectedShipment.lineItems
        .every(item => item.isPacked || item.isCancelled)
    ;

    const itemsWithSameEan = packingStep.selectedShipment.lineItems
        .filter(item => item.eanCode === lineItem.eanCode)
    ;

    const areAllItemsWithSameEanPackedOrCancelled = itemsWithSameEan
        .every(item => item.isPacked || item.isCancelled)
    ;

    const totalItemsCount = itemsWithSameEan.filter(item => !item.isCancelled).length;
    const packedItemsCount = itemsWithSameEan.filter(item => item.isPacked).length;

    // lineItem comes from a clone (JSON.parse(JSON.stringify(...))) built in ShipmentMultiPacking:
    // it never reflects mutations made to packingStep.selectedShipment.lineItems after the drawer is opened.
    const currentLineItem = packingStep.selectedShipment.lineItems.find(
        (item) => item.wmsItem.id === lineItem.wmsItem.id
    )
    const isLineItemCancelled = currentLineItem ? currentLineItem.isCancelled : lineItem.isCancelled

    const isMountedRef = useRef(false);
    const wasCancelledRef = useRef(isLineItemCancelled);
    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            wasCancelledRef.current = isLineItemCancelled;
            return;
        }

        // Only close on the false -> true transition, never if the item was already cancelled when the drawer opened.
        const wasJustCancelled = !wasCancelledRef.current && isLineItemCancelled
        wasCancelledRef.current = isLineItemCancelled

        if (packingStep.selectedShipment.isMulti && wasJustCancelled) {
            handleClose()
            return
        }

        if (
            packingStep.selectedShipment.isMulti &&
            totalItemsCount &&
            packedItemsCount &&
            0 < totalItemsCount &&
            !isLineItemCancelled &&
            packedItemsCount === totalItemsCount
        ) {
            handleClose()
        }
    }, [packedItemsCount, packingStep, totalItemsCount, handleClose, isLineItemCancelled]);

    return (
        <WmsItemInformation wmsItem={lineItem.wmsItem}>
            <Grid container direction={"column"} gap={"16px"}>
                {packingStep.stepName === packingStepNames.PEEK && (
                    <CustomButton fullWidth onClick={beginPacking}>
                        {/* Scan workstation */}
                        <FormattedMessage id={"wms.packing.item.button.beginPacking"}/>
                    </CustomButton>
                )}
                {packingStep.stepName === packingStepNames.PACK_ITEMS && !lineItem.isCancelled && (
                    <CustomButton
                        fullWidth
                        onClick={areAllItemsPackedOrCancelled ? beginShipmentWeigh : () => handlePackItem(lineItem.wmsItem.barcode)}
                        disabled={packingLoading}
                    >
                        {/* Scan workstation */}
                        {areAllItemsPackedOrCancelled ? (
                            <FormattedMessage id={"wms.packing.item.startWeighingShipment"}/>
                        ) : (
                            <>
                                <FormattedMessage id={"wms.packing.item.manualConfirm"}/>
                                {packingStep.selectedShipment.isMulti && (
                                    <span>&nbsp;({packedItemsCount}/{totalItemsCount})</span>
                                )}
                            </>
                        )}
                    </CustomButton>
                )}
                {!lineItem.isCancelled && (
                    <CustomButton
                        color={"red"}
                        fullWidth
                        variant={'nude'}
                        onClick={() => setOpenProductProblemDrawer({
                            isOpen: true,
                            product: lineItem,
                            handleProductCancelled: handleProductCancelled ?? handleAllItemAreCancelled
                        })}
                    >
                        <FormattedMessage id={"wms.packing.productProblem.title"}/>
                    </CustomButton>
                )}
            </Grid>
        </WmsItemInformation>
    );
}

export default WmsItemInformationPacking;
